import { useAuthStore } from '../stores';

// First-party brochure engagement beacons, shared by every brochure variant
// (the resident PIB brochure and the lead-scoped TourGuide AI brochure).
//
// Our Drupal DB owns per-brochure content-view engagement; Fathom keeps owning
// aggregate pageviews (its tracking stays on, untouched). Visitor identity is
// an anonymous uuid held in sessionStorage for the tab's lifetime — no IP, no
// device/geo. The visitor's UTC offset travels with each beacon so the activity
// page renders local times with a fixed transform (no tz-database lookup).
//
// The beacon is source-agnostic: the caller supplies a `tracking` object from
// the brochure response — { endpoint, token, identity } — and the beacon posts
// the identity back verbatim. PIB's identity is { residentNid }; the TourGuide
// AI brochure's is { contactHash, communityNid }. Neither the beacon nor the
// hooks need to know which brochure they serve.
//
// Beacons use navigator.sendBeacon with a text/plain body: it survives page
// teardown (start/end fire on load/pagehide) and text/plain dodges the CORS
// preflight a JSON content-type would trigger on the cross-origin POST.

const VISITOR_UUID_KEY = 'ytBrochureVisitorUuid';
const VISIT_KEY_KEY = 'ytBrochureVisitKey';

// Reuse the Fathom-exclusion verdict the app already resolves (see
// useFathomExclusion), so admin/sales visits don't write to our DB.
const FATHOM_FORCE_KEY = 'ytFathomExclude';
const FATHOM_VERDICT_KEY = 'ytFathomExcludeVerdict';

/**
 * Stable anonymous visitor id for this tab. Minted once, then reused.
 */
export const getVisitorUuid = () => {
  let uuid = sessionStorage.getItem(VISITOR_UUID_KEY);
  if (!uuid) {
    uuid = crypto.randomUUID();
    sessionStorage.setItem(VISITOR_UUID_KEY, uuid);
  }
  return uuid;
};

/**
 * Forget this tab's visitor id and the scope it was opened for, so the next
 * beacon mints a fresh one.
 *
 * Carrying one visitor uuid across a change of brochure subject would tie the
 * two visits together in our engagement store, which is precisely the linkage
 * the anonymous uuid exists to avoid. Brochure mounts go through
 * beginVisitorScope, which resets on that boundary for them; this is the
 * unconditional form.
 */
export const resetVisitorUuid = () => {
  try {
    sessionStorage.removeItem(VISITOR_UUID_KEY);
    sessionStorage.removeItem(VISIT_KEY_KEY);
  } catch (error) {
    // Storage blocked; getVisitorUuid will mint per call, which is no worse.
  }
};

/**
 * Open a visitor scope for `visitKey`, resetting the uuid when the tab was
 * last used for a different brochure subject.
 *
 * The guard lives in sessionStorage rather than in a component ref on purpose:
 * the uuid it guards has tab lifetime, and a ref has mount lifetime. A ref
 * reinitializes to null when BrochurePage unmounts and remounts, so a second
 * brochure opened in the same tab silently reused the first visit's uuid and
 * rewrote its row (#1129). Same lifetime for the guard and the value guarded.
 *
 * @param {string} visitKey - Identity of the visit being opened.
 */
export const beginVisitorScope = (visitKey) => {
  try {
    const previous = sessionStorage.getItem(VISIT_KEY_KEY);
    if (previous && previous !== visitKey) {
      sessionStorage.removeItem(VISITOR_UUID_KEY);
    }
    sessionStorage.setItem(VISIT_KEY_KEY, visitKey);
  } catch (error) {
    // Storage blocked; getVisitorUuid mints per call, so visits can't bleed.
  }
};

/**
 * Visitor's UTC offset as an ISO 8601 string, e.g. '-05:00'.
 * getTimezoneOffset() returns minutes *behind* UTC (positive when behind), so
 * negate it to match the sign convention used in ISO offsets.
 */
export const getUtcOffset = () => {
  const totalMinutes = -new Date().getTimezoneOffset();
  const sign = totalMinutes < 0 ? '-' : '+';
  const abs = Math.abs(totalMinutes);
  const hours = String(Math.floor(abs / 60)).padStart(2, '0');
  const minutes = String(abs % 60).padStart(2, '0');
  return `${sign}${hours}:${minutes}`;
};

/**
 * Visitor's IANA time zone, e.g. 'America/Denver'. Empty when the browser
 * won't say.
 *
 * Sent alongside the offset, not instead of it: an offset says how far from
 * UTC the visitor was at that moment, but not which zone they were in, and a
 * CRM that stores an activity's start and end needs the zone to put them on
 * the right clock. Without it the times land in the CRM portal's own zone.
 */
export const getTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch (error) {
    return '';
  }
};

/**
 * Dev escape hatch: REACT_APP_TRACK_EXCLUDED_VISITS=true makes every visit
 * trackable, so engagement can be exercised from a logged-in admin browser on
 * an excluded IP without incognito windows or clearing fathom_excluded_ips.
 * Ignored when REACT_APP_IS_PRODUCTION is 'true' — it can never weaken the
 * exclusion gate on a production build. Fathom's own gate is untouched.
 */
const exclusionOverridden = () => (
  process.env.REACT_APP_TRACK_EXCLUDED_VISITS === 'true'
  && process.env.REACT_APP_IS_PRODUCTION !== 'true'
);

/**
 * Mirror the Fathom exclusion verdict: don't write engagement for
 * admin/sales-staff visits (?ref=admin, logged-in, or the IP-based verdict).
 * Fail-open (track) when no verdict has resolved yet, matching the Fathom gate.
 */
const isExcluded = () => {
  if (exclusionOverridden()) return false;
  const params = new URLSearchParams(window.location.search);
  if (params.get('ref') === 'admin') {
    sessionStorage.setItem(FATHOM_FORCE_KEY, '1');
  }
  if (sessionStorage.getItem(FATHOM_FORCE_KEY) === '1') return true;
  if (useAuthStore.getState().isLoggedIn) return true;
  return sessionStorage.getItem(FATHOM_VERDICT_KEY) === 'exclude';
};

/**
 * Post one engagement beacon to Drupal. No-op when the tracking creds are
 * missing or the visit is excluded.
 *
 * @param {'start'|'view'|'end'} type - Beacon type.
 * @param {Object} args
 * @param {Object} args.tracking - Creds from the brochure response.
 * @param {string} args.tracking.endpoint - Ingest path, e.g. '/pib-event'.
 * @param {string} args.tracking.token - HMAC token gating the ingest endpoint.
 * @param {Object} args.tracking.identity - Brochure identity echoed back to the
 *   server (e.g. { residentNid } or { contactHash, communityNid }).
 * @param {number} [args.contentNid] - Node the event refers to (view beacons).
 * @param {number} [args.dwellSeconds] - Seconds dwelled (view/about beacons).
 * @param {string} [args.label] - Human-readable subject: link/button text
 *   (click) or bucket title (about).
 * @param {string} [args.target] - Click destination URL (click) or bucket key
 *   (about).
 */
export const sendBrochureBeacon = (type, {
  tracking, contentNid, dwellSeconds, label, target,
} = {}) => {
  if (!tracking || !tracking.endpoint || !tracking.token || !tracking.identity) return;
  if (typeof navigator === 'undefined' || !navigator.sendBeacon) return;
  if (isExcluded()) return;

  const payload = {
    type,
    uuid: getVisitorUuid(),
    ...tracking.identity,
    token: tracking.token,
    utcOffset: getUtcOffset(),
  };
  const timeZone = getTimeZone();
  if (timeZone) payload.timeZone = timeZone;
  if (contentNid) payload.contentNid = contentNid;
  if (typeof dwellSeconds === 'number') payload.dwellSeconds = dwellSeconds;
  if (label) payload.label = label;
  if (target) payload.target = target;

  const url = `${process.env.REACT_APP_YOUR_TOUR_CMS_URL}${tracking.endpoint}`;
  const blob = new Blob([JSON.stringify(payload)], { type: 'text/plain' });
  navigator.sendBeacon(url, blob);
};

/**
 * Delegated click capture for links inside dangerouslySetInnerHTML / mixed
 * content (About sections + captions + resources, the Amenities & Services
 * blurb). Attach as an onClick on the container; when the click lands on (or
 * inside) an <a href>, fire a 'click' beacon carrying the link text + href.
 * A no-op for clicks that aren't on a link, so it's safe on a whole subtree.
 *
 * @param {Object} event - React synthetic (or native) click event.
 * @param {Object} tracking - Brochure tracking creds.
 * @param {number} [contentNid] - Node the content belongs to, when applicable.
 */
export const trackContentLinkClick = (event, tracking, contentNid) => {
  const anchor = event?.target?.closest?.('a[href]');
  if (!anchor) return;
  const target = anchor.getAttribute('href');
  if (!target) return;
  const label = (anchor.textContent || '').trim().slice(0, 255) || null;
  sendBrochureBeacon('click', {
    tracking, label, target, contentNid,
  });
};
